// Copyright 2026, Auxilio Technologies LLC

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../../services/api';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  email: string;
}

export const EmailVerificationModal: React.FC<Props> = ({ isOpen, onClose, onSuccess, email }) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authAPI.verifyEmail(email, code);
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('auth.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    try {
      await authAPI.resendVerificationEmail(email);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('auth.errorGeneric'));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-gray-900">{t('auth.verifyTitle')}</h3>
        <p className="mt-1 text-sm text-gray-500">{t('auth.verifyBody', { email })}</p>

        {error && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleVerify} className="mt-4 space-y-4">
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="••••••"
            className="w-full rounded-lg border border-gray-300 px-4 py-3 text-center text-2xl tracking-[0.5em] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-rose-500"
            required
          />
          <button
            type="submit"
            disabled={loading || code.length < 6}
            className="w-full rounded-lg bg-rose-600 py-3 font-medium text-white transition hover:bg-rose-700 disabled:opacity-50"
          >
            {loading ? t('auth.processing') : t('auth.verify')}
          </button>
        </form>

        <div className="mt-4 flex items-center justify-between text-sm">
          <button onClick={handleResend} className="text-rose-600 hover:text-rose-700">
            {t('auth.resend')}
          </button>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationModal;
